import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/messages`;

// Coach messaging drawer. Auth-gated: requires a signed-in Google user;
// the parent should call openSignIn() before setting `open` to true.
export default function ChatDrawer({ coachSlug, coachName, open, onClose }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const email = user?.email || "";
  const name = user?.name || "";

  const load = async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/thread`, { params: { coach_slug: coachSlug, client_email: email } });
      setMsgs(r.data.messages || []);
      await axios.post(`${API}/read`, { reader_type: "client", reader_email: email, coach_slug: coachSlug, client_email: email });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load conversation.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && email) load(); /* eslint-disable-next-line */ }, [open, email, coachSlug]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!email) return; // Should never happen — gated by parent.
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coach/${coachSlug}/send`, {
        client_email: email, client_name: name || null, body: body.trim(),
      });
      setMsgs((prev) => [...prev, r.data.message]);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send.");
    } finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-drawer"
            className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl"
          >
            <header className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="eyebrow">§ Chat</div>
                <div className="font-display text-lg tracking-tight text-slate-900 mt-1">Message {coachName}</div>
                <p className="mt-1 text-[11.5px] text-slate-400">Signed in as {email}</p>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
              {loading ? (
                <div className="text-center text-[13px] text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
              ) : msgs.length === 0 ? (
                <div className="text-center py-14">
                  <MessageSquare className="w-8 h-8 mx-auto text-slate-300" strokeWidth={1.5} />
                  <p className="mt-3 text-[13px] text-slate-500">Send a message to start the conversation.</p>
                </div>
              ) : (
                msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "client" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                      m.sender_type === "client" ? "bg-[#2563EB] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                    }`}>
                      <div>{m.body}</div>
                      <div className={`mt-1 text-[10.5px] ${m.sender_type === "client" ? "text-white/60" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={send} className="border-t border-slate-200 p-4 space-y-2 bg-white">
              <div className="flex gap-2">
                <input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="chat-body"
                  placeholder="Type a message…"
                  maxLength={2000}
                  className="flex-1 h-11 rounded-full bg-slate-50 border border-slate-200 px-4 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                />
                <button type="submit" disabled={busy || !body.trim()} data-testid="chat-send" className="h-11 w-11 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white grid place-items-center transition-colors disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10.5px] text-slate-400 text-center">Text only · No attachments yet</p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
